import { getAccessToken, setRefreshOwnerCheck } from '../api/client';

/**
 * Die Person, die an diesem Gerät zuletzt angemeldet war (nur ihre Kennung, keine Personendaten). Die Kasse und die Stationen
 * ordnen wartende Vorgänge damit der Person zu, die sie erfasst hat, und senden sie nie unter dem Konto der nächsten Person
 * (Regel 10: Zuordnung des Kassierers). Sie überlebt den Start ohne Netz (Zustand „offline“: das Konto ist dann noch nicht
 * geprüft); Abmelden und Sitzungsende löschen sie.
 */
const KEY = 'barso.auth.person';

const memory: { id: string | null } = { id: null };
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of [...listeners]) listener();
}

export function rememberPerson(userId: string): void {
  memory.id = userId;
  try {
    localStorage.setItem(KEY, userId);
  } catch {
    // ohne Speicher gilt die Person nur in diesem Fenster
  }
  // Auch bei gleicher Person: Die Anmeldung ist jetzt bestätigt (Zugriffstoken da), wartende Vorgänge dürfen hinaus
  notify();
}

export function forgetPerson(): void {
  const changed = currentPerson() !== null;
  memory.id = null;
  try {
    localStorage.removeItem(KEY);
  } catch {
    // nichts zu entfernen
  }
  if (changed) notify();
}

/**
 * Kennung der angemeldeten (oder zuletzt angemeldeten, noch nicht abgemeldeten) Person; `null`, wenn niemand. Hat dieser Tab ein
 * Zugriffstoken, zählt die Person dieses Tabs (Speicher): Meldet sich in einem anderen Tab jemand anderes an, ändert sich der
 * gemeinsame Speicher, dieser Tab sendet aber weiter nur mit seinem eigenen Token. Der gemeinsame Speicher gilt nur für den Start
 * ohne Netz (noch kein Token).
 */
export function currentPerson(): string | null {
  if (getAccessToken() !== null && memory.id !== null) return memory.id;
  try {
    const stored = localStorage.getItem(KEY);
    if (stored !== null && stored !== '') return stored;
  } catch {
    // Speicher gesperrt: der Wert im Speicher des Fensters zählt
  }
  return memory.id;
}

/** Diesen Tab ohne Person lassen, ohne den gemeinsamen Speicher anzufassen (er gehört jetzt dem anderen Tab). */
export function dropTabPerson(): void {
  const changed = memory.id !== null;
  memory.id = null;
  if (changed) notify();
}

const foreignListeners = new Set<() => void>();

/** Wird gerufen, wenn ein anderer Tab eine andere Person angemeldet hat als dieser Tab (dieser Tab beendet dann seine Sitzung lokal). */
export function onForeignPerson(listener: () => void): () => void {
  foreignListeners.add(listener);
  return () => {
    foreignListeners.delete(listener);
  };
}

// Stilles Erneuern über das Cookie: Gehört das neue Token einer anderen Person als dieser Tab, beendet der Tab seine Sitzung lokal
setRefreshOwnerCheck((owner) => {
  if (owner === null || memory.id === null || memory.id === owner) return true;
  for (const listener of [...foreignListeners]) listener();
  return false;
});

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== KEY || event.newValue === null || event.newValue === '') return;
    if (memory.id === null || memory.id === event.newValue) return;
    for (const listener of [...foreignListeners]) listener();
  });
}

/** Wird gerufen, wenn eine Person angemeldet ist oder sich abmeldet (Anmeldung, Start mit gültiger Sitzung, Abmeldung): z. B. um wartende Vorgänge der neuen Person zu senden. */
export function onPersonChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
